import type { SchedulerAppointment, SchedulerAppointmentSlot, TypeDate, TypeTimeOfDay } from '@/types/api'

export type ViewingAppointmentCalendarProvider = 'google' | 'outlook' | 'apple' | 'ics'

export type ViewingCalendarTimedItem = Pick<SchedulerAppointmentSlot, 'id' | 'date' | 'startTime' | 'endTime'> | Pick<SchedulerAppointment, 'id' | 'date' | 'startTime' | 'endTime'>

export interface ViewingCalendarEventParams {
  appointment: ViewingCalendarTimedItem
  title: string
  location: string
  url: string
}

function pad(n: number): string {
  return ('0' + (n || 0)).slice(-2)
}

function compactLocal(date: TypeDate, time: TypeTimeOfDay): string {
  return `${date.year || 0}${pad(date.month || 0)}${pad(date.day || 0)}T${pad(time.hours || 0)}${pad(time.minutes || 0)}00`
}

function isoLocal(date: TypeDate, time: TypeTimeOfDay): string {
  return `${date.year || 0}-${pad(date.month || 0)}-${pad(date.day || 0)}T${pad(time.hours || 0)}:${pad(time.minutes || 0)}:00`
}

function escapeIcs(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
}

export function buildGoogleCalendarUrl(params: ViewingCalendarEventParams): string {
  const { appointment, title, location, url } = params
  const { date, startTime, endTime } = appointment
  if (!date || !startTime || !endTime) {
    return ''
  }
  const query = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${compactLocal(date, startTime)}/${compactLocal(date, endTime)}`,
    location,
    details: url,
    ctz: 'Europe/Zurich'
  })
  return `https://calendar.google.com/calendar/render?${query.toString()}`
}

export function buildOutlookCalendarUrl(params: ViewingCalendarEventParams): string {
  const { appointment, title, location, url } = params
  const { date, startTime, endTime } = appointment
  if (!date || !startTime || !endTime) {
    return ''
  }
  const query = new URLSearchParams({
    rru: 'addevent',
    subject: title,
    startdt: isoLocal(date, startTime),
    enddt: isoLocal(date, endTime),
    location,
    body: url,
    path: '/calendar/action/compose'
  })
  return `https://outlook.live.com/calendar/0/deeplink/compose?${query.toString()}`
}

export function buildIcsContent(params: ViewingCalendarEventParams): string {
  const { appointment, title, location, url } = params
  const { date, startTime, endTime } = appointment
  if (!date || !startTime || !endTime) {
    return ''
  }
  const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
  const uid = appointment.id
    ? escapeIcs(appointment.id)
    : compactLocal(date, startTime) + '@propbase'
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//PropBase//Viewing Appointments//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;TZID=Europe/Zurich:${compactLocal(date, startTime)}`,
    `DTEND;TZID=Europe/Zurich:${compactLocal(date, endTime)}`,
    `SUMMARY:${escapeIcs(title)}`,
    `LOCATION:${escapeIcs(location)}`,
    `URL:${escapeIcs(url)}`,
    `DESCRIPTION:${escapeIcs(url)}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n')
}

export function downloadIcsFile(params: ViewingCalendarEventParams): void {
  const content = buildIcsContent(params)
  if (!content) {
    return
  }
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = 'viewing-appointment.ics'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(objectUrl)
}

export function openViewingAppointmentCalendar(
  params: ViewingCalendarEventParams,
  provider: ViewingAppointmentCalendarProvider
): void {
  if (provider === 'google') {
    const url = buildGoogleCalendarUrl(params)
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    return
  }
  if (provider === 'outlook') {
    const url = buildOutlookCalendarUrl(params)
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    return
  }
  downloadIcsFile(params)
}

export function formatViewingObjectAddress(address?: {
  careOf?: string
  street?: string
  streetNumber?: string
  zip?: string
  city?: string
} | null): string {
  if (!address) {
    return ''
  }
  const street = [address.street, address.streetNumber].filter(Boolean).join(' ')
  const city = [address.zip, address.city].filter(Boolean).join(' ')
  return [address.careOf, street, city].filter(Boolean).join(', ')
}
